import { useEffect, useState } from 'react'
import to from 'await-catch'
import moment from 'moment'
import { getAvailableAppointments } from '../api'
import { cleanParams } from '../utils/cleanParams'

const getSpecialtiesToSearch = filters => {
  if (Array.isArray(filters?.specialities) && filters.specialities.length > 0) {
    return filters.specialities
  }

  return filters?.speciality ? [filters.speciality] : []
}

const formatTimeToHHmm = timeValue => {
  if (!timeValue) return null

  if (typeof timeValue === 'string') {
    return timeValue.slice(0, 5)
  }

  if (timeValue instanceof Date) {
    const hours = String(timeValue.getHours()).padStart(2, '0')
    const minutes = String(timeValue.getMinutes()).padStart(2, '0')
    return `${hours}:${minutes}`
  }

  return null
}

const resolvePatientPlanData = patient => ({
  idObraSocial: patient?.idObraSocial || patient?.obraSocial?.id,
  idPlan: patient?.idPlan || patient?.plan?.id
})

const buildParams = ({ patient, filters, specialityId }) => {
  const fechaDesde = filters?.dateFrom
    ? moment(filters.dateFrom).format('YYYY-MM-DD')
    : moment().format('YYYY-MM-DD')
  const fechaHasta = filters?.dateTo
    ? moment(filters.dateTo).format('YYYY-MM-DD')
    : moment()
        .add(60, 'days')
        .format('YYYY-MM-DD')

  let horaDesde
  let horaHasta

  if (filters?.hourFrom && filters?.hourTo) {
    horaDesde = formatTimeToHHmm(filters.hourFrom)
    horaHasta = formatTimeToHHmm(filters.hourTo)
  } else {
    horaDesde = '00:00'
    horaHasta = '23:59'
  }

  const clinicaParam = Array.isArray(filters?.clinic)
    ? filters.clinic.filter(Boolean).join(',')
    : filters?.clinic

  const { idObraSocial, idPlan } = resolvePatientPlanData(patient)

  return cleanParams({
    idObraSocial,
    idPlan,
    clinicas: clinicaParam,
    idEspecialidad: specialityId,
    idProfesional: filters?.professional,
    fechaDesde,
    fechaHasta,
    horaDesde,
    horaHasta,
    idSistema: ''
  })
}

/**
 * Obtiene los turnos disponibles para los filtros indicados.
 * Re-ejecuta la busqueda cada vez que cambia trigger.
 */
export const useAvailableAppointments = ({
  patient,
  filters,
  trigger,
  enabled = true,
  existingResults = [],
  showLoader,
  hideLoader,
  addFlashMessage
}) => {
  const [appointments, setAppointments] = useState([])
  const [hasFetched, setHasFetched] = useState(false)

  useEffect(() => {
    if (!enabled) {
      setHasFetched(false)
      return
    }

    const { idObraSocial, idPlan } = resolvePatientPlanData(patient)
    if (!idObraSocial || !idPlan) {
      setAppointments([])
      setHasFetched(true)
      return
    }

    const fetchTurnos = async () => {
      setHasFetched(false)

      try {
        showLoader()

        const specialtiesToSearch = getSpecialtiesToSearch(filters)

        if (specialtiesToSearch.length === 0) {
          setAppointments([])
          setHasFetched(true)
          return
        }

        const responses = await Promise.all(
          specialtiesToSearch.map(specialityId => {
            const params = buildParams({ patient, filters, specialityId })
            return to(getAvailableAppointments(params))
          })
        )

        const hadAnyError = responses.some(([err]) => !!err)

        const mergedTurnos = responses.flatMap(([, data]) =>
          Array.isArray(data?.data)
            ? data.data.flatMap(dayGroup =>
                Array.isArray(dayGroup?.turnos) ? dayGroup.turnos : []
              )
            : []
        )

        const dedupedTurnos = Array.from(
          new Map(mergedTurnos.map(turn => [turn.idTurno, turn])).values()
        )

        if (hadAnyError) {
          addFlashMessage('Ocurrió un error cargando algunos turnos')
        }

        if (filters?.mergeIntoExisting) {
          const queriedSpecialties = new Set(specialtiesToSearch.map(String))
          const base = Array.isArray(existingResults) ? existingResults : []
          const kept = base.filter(
            turn => !queriedSpecialties.has(String(turn.idEspecialidad))
          )
          const combined = [...kept, ...dedupedTurnos]
          setAppointments(
            Array.from(new Map(combined.map(turn => [turn.idTurno, turn])).values())
          )
        } else {
          setAppointments(dedupedTurnos)
        }
        setHasFetched(true)
      } catch (error) {
        addFlashMessage('Ocurrió un error cargando los turnos')
        setAppointments([])
        setHasFetched(true)
      } finally {
        hideLoader()
      }
    }

    fetchTurnos()
    // Solo se dispara con el trigger de busqueda para evitar re-fetches accidentales.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trigger, enabled])

  return { appointments, hasFetched }
}
